import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';
import AntPopup from './AntPOSPopup';
import AntBaseForm from './AntPOSForm';
import AntConfig from '../utils/AntConfig';
// import AntAuth from '../utils/AntAuth';
import AntCondition from '../utils/AntCondition';
import AntLocalization from '../utils/AntLocalization';

import { AntGlobal, AntSession } from '../utils/AntContext';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    // const session = AntSession();

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';
    const entityName = props.API && props.API.entityName ? props.API.entityName : '';
    const entityDisplay = props.API && props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay ? props.entityDisplay : '';
    const popupTitle = props.title
        ? AntLocalization.translateText(props.title)
        : `${AntLocalization.translateText({ vi: 'Chỉnh sửa ', en: 'Edit', fr: 'Modifier', es: 'Editar', id: 'Edit', ja: '編集', zh: '编辑', ko: '편집', hi: 'संपादित करें', ar: 'تعديل' })} ${AntLocalization.translateText(entityDisplay)}`;

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const popup = useRef();
    const form = useRef();

    const [mainError, setMainError] = useState({ error: null });
    const [mainData, setMainData] = useState({});
    const [oldData, setOldData] = useState({});

    const [shouldRender, setShouldRender] = useState(1);

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const onCustomMainData = useCallback((data, changeData, overide) => {
        if (overide) {
            if (data && typeof data === 'object') {
                let newObj = JSON.parse(stringify(changeData));

                let keys = Object.keys(data);
                for (let i = 0; i < keys.length; i++) {
                    const key = keys[i];
                    delete data[key];
                }

                return Object.assign(data, newObj);
            } else return changeData;
        }

        return Object.assign(data, changeData);
    }, []);

    const forceRenderWithData = useCallback(
        (data, overide) => {
            setMainData((v) => onCustomMainData(v, data, overide));
            // setShouldRender((v) => v + 1);
        },
        [onCustomMainData]
    );

    const isDisableForm = useCallback(() => {
        if (!mainData || !mainData.id) return false;

        if (mainData && mainData.edit && mainData.edit.always_editable_fields instanceof Array && mainData.edit.always_editable_fields.length > 0)
            return false;

        var disabled =
            props.disabled ||
            !mainData ||
            !mainData.id ||
            !mainData.edit ||
            mainData.edit.errors ||
            !mainData.edit.isEditable ||
            (mainData.edit.editable_fields && mainData.edit.editable_fields instanceof Array && mainData.edit.editable_fields.length <= 0);

        if (disabled) return true;

        return false;
    }, [mainData, props.disabled]);

    const initPopupError = useCallback(() => {
        var isEditable = !isDisableForm();
        if (!isEditable)
            return {
                text: AntLocalization.translateText({ vi: 'Không thể sửa thông tin !', en: 'Cannot edit data !', fr: 'Modification impossible !', es: '¡No se puede editar!', id: 'Tidak dapat diubah !', ja: '編集できません！', zh: '无法修改信息！', ko: '수정할 수 없습니다!', hi: 'संपादन संभव नहीं !', ar: 'لا يمكن التعديل !' }),
                values: mainData && mainData.edit && mainData.edit.errors && mainData.edit.errors instanceof Array ? mainData.edit.errors : []
            };

        if (mainError.error)
            return {
                text: mainError.error.text,
                values: mainError.error.values
            };
        return null;
    }, [mainError, mainData, isDisableForm]);

    const popupError = useMemo(() => initPopupError(), [initPopupError]);

    const initButtons = useCallback(() => {
        var isEditable = !isDisableForm();

        if (isEditable)
            return [
                {
                    id: 'btnSave',
                    type: 'button',
                    icon: 'save',
                    text: AntLocalization.translateText({ vi: 'Cập nhật', en: 'Save', fr: 'Enregistrer', es: 'Guardar', id: 'Simpan', ja: '保存', zh: '保存', ko: '저장', hi: 'सहेजें', ar: 'حفظ' }),
                    waiting_text: AntLocalization.translateText({ vi: 'Chờ xử lý', en: 'Processing', fr: 'Traitement', es: 'Procesando', id: 'Memproses', ja: '処理中', zh: '处理中', ko: '처리 중', hi: 'प्रसंस्करण', ar: 'جاري المعالجة' }),
                    width: 130
                }
            ];
        return [];
    }, [isDisableForm]);

    const popupButtons = useMemo(() => initButtons(), [initButtons]);

    const initSize = useCallback(() => {
        var formColCount = AntCondition.getValue(mainData, props.colCount);
        if (formColCount === undefined) formColCount = props.colCountDefault ? props.colCountDefault : 1;

        var formItems = props.items instanceof Array ? props.items : [];
        formItems = JSON.parse(stringify(formItems));
        formItems = formItems.filter((item) => {
            return AntCondition.isVisible(mainData, item);
        });

        var sumSpan = formItems.reduce((total, current) => {
            var colSpan = AntCondition.getValue(mainData, current.colSpan);
            if (colSpan === undefined) colSpan = current.colSpanDefault;

            return total + (colSpan !== undefined ? parseInt(colSpan) : 1);
        }, 0);

        var formLineCount = Math.ceil(sumSpan / formColCount);
        var formHeight = 0;
        if (formLineCount > 0) {
            formHeight = formLineCount * 36 + (formLineCount - 1) * 10;
            formHeight += formItems.reduce((total, current) => {
                return (
                    total +
                    (current.editorType === 'textArea' && current.options && current.options.height > 36 ? parseInt(current.options.height) - 36 : 0)
                );
            }, 0);
        }

        var minHeight;
        if (props.height instanceof Array) {
            for (let i = 0; i < props.height.length; i++) {
                const e = props.height[i];
                if (e && typeof e.when === 'object' && e.when !== null && e.value !== undefined) {
                    var isValid = AntCondition.isValid(mainData, {
                        when: e.when,
                        whenType: e.whenType
                    });
                    if (isValid) {
                        minHeight = parseFloat(e.value);
                        break;
                    }
                }
            }
        } else if (typeof props.height === 'number') minHeight = parseFloat(props.height);

        var height = 0;
        height += formHeight ? parseFloat(formHeight) : 0;
        height += 10 + 46 + 20 + 47;
        height += 10;

        if (minHeight && height < minHeight) height = minHeight;

        if (popupError) height += 20;

        var width = AntCondition.getValue(mainData, props.width);
        if (width === undefined) width = props.widthDefault;
        if (width === undefined) width = 700;

        return { width: width, height: height, minHeight: minHeight, maxHeight: props.maxHeight };
    }, [
        mainData,
        props.colCount,
        props.colCountDefault,
        props.items,
        props.height,
        props.width,
        props.widthDefault,
        props.minHeight,
        props.maxHeight
    ]);

    const popupSize = useMemo(() => initSize(), [initSize]);

    const show = useCallback(
        async (obj, recalcable) => {
            if (!obj) return;
            if (!popup.current) return;
            if (!obj.id && !obj.code) return;

            var preparedObj = null;
            if (props.prepare !== false && props.API && props.API.postUpdatePrepare) {
                preparedObj = await props.API.postUpdatePrepare(
                    obj.id,
                    obj.code,
                    Boolean(recalcable),
                    props.prepareConditions,
                    mainData
                );
            }
            if (preparedObj) {
                if (props.onPrepared) await props.onPrepared(preparedObj);

                let oldObj = JSON.parse(stringify(preparedObj));
                delete oldObj.lookups;
                delete oldObj.edit;
                setOldData(oldObj);

                forceRenderWithData(preparedObj, true);

                popup.current.show();

                return;
            }
        },
        [props.prepare, props.API, props.prepareConditions, props.onPrepared, forceRenderWithData, mainData]
    );

    const showById = useCallback(
        (id, recalcable) => {
            if (!id) return;

            return show({ id: id }, recalcable);
        },
        [show]
    );

    const onFormError = useCallback(
        async (content, errors) => {
            if (!content && (popupError === null || popupError === undefined)) return;

            if (errors == 'TypeError: Failed to fetch') errors = { vi: 'Không thể kết nối đến hệ thống !', en: 'Can not connect to system !', fr: 'Impossible de se connecter au système !', es: '¡No se puede conectar al sistema!', id: 'Tidak dapat terhubung ke sistem!', ja: 'システムに接続できません！', zh: '无法连接到系统！', ko: '시스템에 연결할 수 없습니다!', hi: 'सिस्टम से कनेक्ट नहीं हो सकता!', ar: 'لا يمكن الاتصال بالنظام!' };

            var values = errors === undefined || errors === null ? [] : errors instanceof Array ? errors : [errors];

            content = values.length === 1 ? values[0] : content;

            setMainError({
                error: content
                    ? {
                          text: String(content),
                          values: values
                      }
                    : null
            });
        },
        [popupError]
    );

    const onFieldDataChanged = useCallback(
        async (e, newData) => {
            if (props.onFieldDataChanged) {
                var isValid = await props.onFieldDataChanged(e);
                if (!isValid) return;
            }

            forceRenderWithData(newData);
        },
        [props.onFieldDataChanged, forceRenderWithData]
    );

    const onAction = useCallback(
        async (button) => {
            if (!form.current) return false;

            if (form.current.validate) {
                var isValid = await form.current.validate();
                if (!isValid) return false;
            }

            var close = false;
            if (form.current.onAction) close = await form.current.onAction(button);

            // if (!close && props.onAction) close = await props.onAction(button);

            return close;
        },
        [props.onAction]
    );

    useImperativeHandle(ref, () => ({
        showById: showById,
        show: show
    }));

    useEffect(() => {
        if (props.onError) props.onError(content, errors);
    }, [popupError]);

    return (
        <React.Fragment>
            {shouldRender && (
                <AntPopup
                    ref={popup}
                    shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                    isGray={true}
                    deferRendering={true}
                    height={props.height ? props.height : popupSize.height}
                    width={popupSize.width}
                    minHeight={popupSize.minHeight}
                    // maxHeight={popupSize.maxHeight}
                    autoHeight={true}
                    autoWidth={true}
                    title={popupTitle}
                    onAction={onAction}
                    name={`ant-object-edit-error-${entityName}`}
                    errors={popupError ? popupError.values : undefined}
                    errorText={AntLocalization.translateText(popupError ? popupError.text : undefined)}
                    hideOnOutsideClick={props.hideOnOutsideClick !== undefined ? props.hideOnOutsideClick : true}
                    widgets={popupButtons}
                >
                    <div style={{ padding: '14px 16px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <AntBaseForm
                        ref={form}
                        shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                        API={props.API}
                        type={'Update'}
                        table={props.table}
                        default={props.default}
                        parentData={props.parentData}
                        parentFK={props.parentFK}
                        copyFromParentFields={props.copyFromParentFields}
                        formData={mainData}
                        oldData={oldData}
                        alignItemLabelsInAllGroups={props.alignItemLabelsInAllGroups}
                        labelLocation={props.labelLocation}
                        colCount={props.colCount ? props.colCount : 1}
                        colCountDefault={props.colCountDefault}
                        onFieldDataChanged={onFieldDataChanged}
                        validationURL={props.validationURL}
                        actionURL={props.actionURL}
                        onValidation={props.onValidation}
                        onSaving={props.onSaving}
                        onProcess={props.onProcess}
                        onSaved={props.onSaved}
                        fields={props.fields}
                        items={props.items}
                        postFields={props.postFields instanceof Array ? props.postFields : undefined}
                        onError={onFormError}
                    />
                    </div>
                </AntPopup>
            )}
        </React.Fragment>
    );
});
